// scheduling/utils/backlogSelectors.js
// Pure selectors for the two backlog lenses. Extracted from StaffDashboard's inline
// memos so the SAME computation drives both the rendered list (BacklogView) and the
// tab badge (Dashboard owner) — the badge can never drift from the list again.
//
// Both take firm-wide inputs (all jobs / all WIP / all schedule) and do the
// per-viewer filtering internally, so they work off the unified data layer.

import { isBacklogJob, isMyTimeJob, isLeadOf } from './schedConstants';

function toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }

/**
 * My Jobs backlog (JM/JP lens): jobs the viewer LEADS whose team has started real
 * work and still need scheduling. Returns enriched, sorted job cards.
 */
export function selectMyJobsBacklog({ jobs, jobsWIP, schedule, meStaffId, hidePostReview = true }) {
  const wipByJob = {};
  for (const w of (jobsWIP || [])) {
    if (!w.JobId) continue;
    if (!wipByJob[w.JobId]) wipByJob[w.JobId] = { myHours: 0, totalHours: 0, byStaff: [] };
    const entry = wipByJob[w.JobId];
    entry.totalHours += w.TotalHours || 0;
    if (w.StaffId === meStaffId) entry.myHours += w.TotalHours || 0;
    entry.byStaff.push({ staffId: w.StaffId, name: w.StaffName || 'Unknown', initials: w.StaffInitials || '??', hours: w.TotalHours || 0 });
  }

  // Scheduled hours by job → by staff. Count TODAY-ONWARD only (past scheduled
  // hours are already history/WIP). Firm-wide schedule is fine — only led-job
  // JobIds are ever looked up.
  const schedTodayISO = toISO(new Date());
  const schedByJob = {};
  for (const e of (schedule || [])) {
    const eISO = typeof e.EntryDate === 'string' ? e.EntryDate.slice(0, 10) : (e.EntryDate ? toISO(new Date(e.EntryDate)) : '');
    if (eISO && eISO < schedTodayISO) continue;
    if (!schedByJob[e.JobId]) schedByJob[e.JobId] = { total: 0, staffMap: {} };
    const entry = schedByJob[e.JobId];
    entry.total += e.Hours || 0;
    const key = e.StaffId;
    if (!entry.staffMap[key]) entry.staffMap[key] = { staffId: key, name: e.StaffName || 'Unknown', initials: e.StaffInitials || '??', hours: 0 };
    entry.staffMap[key].hours += e.Hours || 0;
  }

  // Placeholder hours (schedData queryJobs PlaceholderHours, today-onward) count as
  // scheduled here too. From a JM's point of view a placeholder IS scheduled — they
  // allotted the calendar space and handed the naming step to the production committee
  // — so a placeholder-covered job drops out of the backlog rather than nagging them to
  // schedule work they already scheduled.
  const schedTotalFor = (j) => (schedByJob[j.JobId]?.total || 0) + (j.PlaceholderHours || 0);

  return (jobs || [])
    .filter(j => isLeadOf(j, meStaffId))
    .filter(j => isBacklogJob(j, wipByJob[j.JobId], {
      hidePostReview,
      scheduledHours: schedTotalFor(j),
    }))
    .map(j => {
      const budget = j.BudgetHours || 0;
      const wip = wipByJob[j.JobId] || { myHours: 0, totalHours: 0, byStaff: [] };
      const sched = schedByJob[j.JobId] || { total: 0, staffMap: {} };
      const placeholder = j.PlaceholderHours || 0;
      const schedTotal = Math.round((sched.total + placeholder) * 100) / 100;
      const rawRemaining = Math.round((budget - wip.totalHours - schedTotal) * 100) / 100;
      const overBudget = rawRemaining < 0;
      return {
        ...j, budget,
        myWip: Math.round(wip.myHours * 100) / 100,
        othersWip: Math.round((wip.totalHours - wip.myHours) * 100) / 100,
        wipTotal: Math.round(wip.totalHours * 100) / 100,
        wipByStaff: wip.byStaff.filter(s => s.staffId !== meStaffId).sort((a, b) => b.hours - a.hours),
        scheduledTotal: schedTotal,
        placeholderHrs: placeholder,
        scheduledByStaff: Object.values(sched.staffMap).sort((a, b) => b.hours - a.hours),
        remaining: Math.max(0, rawRemaining),
        overBudget,
        overage: overBudget ? Math.abs(rawRemaining) : 0,
        pctComplete: budget > 0 ? Math.round(((wip.totalHours + schedTotal) / budget) * 100) : 0,
      };
    })
    .sort((a, b) => {
      const da = a.Deadline || '9999';
      const db = b.Deadline || '9999';
      if (da !== db) return da < db ? -1 : 1;
      return b.remaining - a.remaining;
    });
}

/**
 * My Time backlog (everyone lens): jobs the viewer personally logged time on
 * recently that still need work and aren't on their schedule. `myTimeWIP` is the
 * ALL-staff WIP rows for jobs the viewer recently worked (from the recentForStaffId
 * query); `myTimeSched` is the by-staff future-scheduled for those jobs. `staffById`
 * resolves JM/JP names. Recency is narrowed client-side to `recentDays`.
 */
export function selectMyTimeBacklog({ myTimeWIP, myTimeSched, staffById = {}, meStaffId, recentDays = 14 }) {
  const byJob = {};
  for (const w of (myTimeWIP || [])) {
    if (!w.JobId) continue;
    let e = byJob[w.JobId];
    if (!e) e = byJob[w.JobId] = { job: w, wipTotal: 0, myWip: 0, myLast: null, wipByStaff: [] };
    e.wipTotal += w.TotalHours || 0;
    if (w.StaffId === meStaffId) {
      e.myWip += w.TotalHours || 0;
      e.job = w;
      const last = typeof w.LastDate === 'string' ? w.LastDate.slice(0, 10) : (w.LastDate ? toISO(new Date(w.LastDate)) : null);
      if (last && (!e.myLast || last > e.myLast)) e.myLast = last;
    } else {
      e.wipByStaff.push({ staffId: w.StaffId, name: w.StaffName || 'Unknown', hours: Math.round((w.TotalHours || 0) * 100) / 100 });
    }
  }
  const schedByJob = {};
  for (const s of (myTimeSched || [])) {
    if (!s.JobId) continue;
    let e = schedByJob[s.JobId];
    if (!e) e = schedByJob[s.JobId] = { total: 0, mine: 0, byStaff: [] };
    e.total += s.TotalHours || 0;
    if (s.StaffId === meStaffId) e.mine += s.TotalHours || 0;
    e.byStaff.push({ staffId: s.StaffId, name: s.StaffName || 'Unknown', hours: Math.round((s.TotalHours || 0) * 100) / 100 });
  }
  const cutoff = toISO(addDays(new Date(), -recentDays));
  return Object.values(byJob)
    .filter(e => e.myWip > 0 && (!e.myLast || e.myLast >= cutoff))
    // Placeholder hours (PlaceholderHours rides on the WIP rows, same today-onward rule
    // as queryJobs) count as scheduled here too, so My Time and My Jobs agree about what
    // still needs scheduling.
    .filter(e => {
      const sc = schedByJob[e.job.JobId];
      return isMyTimeJob(e.job,
        { myHours: e.myWip, jobWipTotal: e.wipTotal, jobFutureSched: (sc?.total || 0) + (e.job.PlaceholderHours || 0) },
        { hasUpcomingSched: (sc?.mine || 0) > 0 });
    })
    .map(e => {
      const j = e.job;
      const budget = j.BudgetHours || 0;
      const sc = schedByJob[j.JobId] || { total: 0, byStaff: [] };
      const placeholder = j.PlaceholderHours || 0;
      const scTotal = Math.round((sc.total + placeholder) * 100) / 100;
      const rawRemaining = Math.round((budget - e.wipTotal - scTotal) * 100) / 100;
      return {
        JobId: j.JobId, Name: j.JobName, ClientName: j.ClientName, ClientId: j.ClientId,
        ServiceDesc: j.ServiceDesc, StatusDetail: j.StatusDetail, Deadline: j.DueDate,
        ManagerName: staffById[j.ManagerId]?.Name || null,
        PartnerName: staffById[j.PartnerId]?.Name || null,
        budget,
        myWip: Math.round(e.myWip * 100) / 100,
        othersWip: Math.round((e.wipTotal - e.myWip) * 100) / 100,
        wipTotal: Math.round(e.wipTotal * 100) / 100,
        scheduledTotal: scTotal,
        placeholderHrs: placeholder,
        remaining: Math.max(0, rawRemaining),
        pctComplete: budget > 0 ? Math.round(((e.wipTotal + scTotal) / budget) * 100) : 0,
        wipByStaff: e.wipByStaff.sort((a, b) => b.hours - a.hours),
        scheduledByStaff: sc.byStaff.slice().sort((a, b) => b.hours - a.hours),
      };
    })
    .sort((a, b) => {
      const da = a.Deadline || '9999';
      const db = b.Deadline || '9999';
      if (da !== db) return da < db ? -1 : 1;
      return b.remaining - a.remaining;
    });
}
